import {useEffect, useState, type RefObject} from "react";

/**
 * Страховка для появления по прокрутке.
 *
 * Появление блоков лендинга ведёт браузерный наблюдатель за кадром
 * (IntersectionObserver под `whileInView`). Он сверяет положение блоков раз в
 * отрисованный кадр — и в этом дыра: если между двумя кадрами страница уехала
 * дальше, чем высота блока, блок за один кадр проскакивает экран насквозь.
 * «Внутри экрана» его не застают ни разу, появление не запускается, а так как
 * оно одноразовое (`once: true`), блок остаётся пустым навсегда. Человек листает
 * обратно вверх — и видит дыру на месте карточек.
 *
 * Это не редкий случай. Так происходит при каждом переходе по пункту меню
 * (шапка прокручивает к разделу через полстраницы), при быстром броске пальцем
 * на телефоне и всегда при системной настройке «уменьшить движение» — там
 * прокрутка к разделу мгновенная, и мимо пролетает всё, что между.
 *
 * Проверено на этой странице: переход из шапки к последнему разделу оставлял
 * 16 блоков пустыми навсегда.
 *
 * Что делает этот модуль: раз в кадр (и только пока есть кого ждать) проверяет
 * блоки, которые ещё не появились, и засчитывает появление тем, кто целиком
 * ушёл за верхнюю кромку экрана. Наверх страница едет только прокруткой, так
 * что «блок выше экрана» означает ровно одно: его пролистнули.
 *
 * Почему не увеличить зону наблюдения вверх на всю страницу вместо этого: часть
 * блоков всё равно теряется — лендинг едет внутри собственного вьюпорта
 * (OverlayScrollbars), и наблюдатель обрезает блоки по его границе, куда бы мы
 * ни растянули зону. Проверка по координатам от этого не зависит.
 */

interface Pending {
    el: HTMLElement;
    done: () => void;
}

const pending = new Set<Pending>();
let frame = 0;

function sweep(): void {
    frame = 0;
    for (const entry of Array.from(pending)) {
        // Целиком выше верхней кромки — значит мимо него уже пролистали.
        if (entry.el.getBoundingClientRect().bottom <= 0) {
            pending.delete(entry);
            entry.done();
        }
    }
    if (pending.size === 0) stopListening();
}

/**
 * Проверка не чаще раза в кадр: прокрутка присылает события пачками, а нам
 * нужно одно измерение на отрисовку. Считать чаще — значит греть телефон и
 * дёргать раскладку без единого лишнего результата.
 */
function schedule(): void {
    if (frame) return;
    frame = requestAnimationFrame(sweep);
}

let listening = false;

function startListening(): void {
    if (listening) return;
    listening = true;
    // Событие прокрутки не всплывает, поэтому слушаем на перехвате: страница
    // едет внутри своего вьюпорта, а не в окне, и до окна событие иначе не
    // дойдёт. Пассивно — обработчик не должен вставать на пути прокрутки
    // (скилл apple-design §1: любая задержка на пути ввода — брак).
    window.addEventListener("scroll", schedule, {capture: true, passive: true});
    window.addEventListener("resize", schedule, {passive: true});
}

function stopListening(): void {
    if (!listening) return;
    listening = false;
    window.removeEventListener("scroll", schedule, {capture: true});
    window.removeEventListener("resize", schedule);
}

/**
 * Вернёт `true`, когда блок пролистнули мимо, не показав.
 *
 * @param ref     блок, за которым следим;
 * @param enabled следить ли вообще — у блоков, которые появляются по загрузке
 *                или по команде родителя, пропускать нечего.
 */
export function useScrolledPast(ref: RefObject<HTMLElement | null>, enabled: boolean): boolean {
    const [passed, setPassed] = useState(false);

    useEffect(() => {
        if (!enabled || passed) return;
        const el = ref.current;
        if (!el) return;

        const entry: Pending = {el, done: () => setPassed(true)};
        pending.add(entry);
        startListening();
        // Первая проверка сразу: страницу могли открыть уже прокрученной —
        // например по ссылке с якорем или при возврате назад.
        schedule();

        return () => {
            pending.delete(entry);
            if (pending.size === 0) stopListening();
        };
    }, [ref, enabled, passed]);

    return passed;
}
